<script setup lang="ts">
import {inject, ref, watch, watchEffect} from 'vue';
import Decimal from 'decimal.js';
import {formatting, LegacyAdjustmentCategory, LegacyOrder, legacyOrderKey} from '@hantera/portal-app';
import {DropdownButton, DropdownItem, TextInput} from '@hantera/design-system';

const props = defineProps<{
  selectedCategory?: LegacyAdjustmentCategory
  value: Decimal
}>();

const emit = defineEmits<{
  (e: 'update:selected-category', value: LegacyAdjustmentCategory): void
  (e: 'update:value', value: Decimal): void
  (e: 'submit'): void
  (e: 'cancel'): void
}>();

defineExpose({
  setValueError() {
    valueElem.value?.setError();
  }
});

const { number, cleanNumberInput } = formatting;

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const valueElem = ref<InstanceType<typeof TextInput>>();
const selectedCategory = ref<LegacyAdjustmentCategory>();
const valueText = ref<string>('');

watchEffect(() => {
  selectedCategory.value = props.selectedCategory;
});
watchEffect(() => {
  valueText.value = number(props.value).replaceAll(' ', '');
});

function select(value: LegacyAdjustmentCategory) {
  selectedCategory.value = value;
  emit('update:selected-category', value);
}

watch(valueText, () => {
  try {
    const newValue = new Decimal(cleanNumberInput(valueText.value || '0'));
    if (!props.value.eq(newValue)) {
      emit('update:value', newValue);
    }
  } catch { }
});

function amountBlur() {
  try {
    const newValue = new Decimal(cleanNumberInput(valueText.value));

    if (!props.value.eq(newValue)) {
      emit('update:value', newValue);
    }
  } catch {
    valueElem.value?.setError();
    return;
  }
}

function amountFocus() {
  valueElem.value?.select();
}

</script>


<template>
  <div class="rma-adjustment-form">
    <DropdownButton :label="selectedCategory?.name || 'Select category...'">
      <DropdownItem
        v-for="c in order.adjustmentCategories"
        @click="select(c)"
      >
        {{ c.name }}
      </DropdownItem>
    </DropdownButton>

    <div class="rma-adjustment-form__amount">
      <TextInput
        v-model="valueText"
        ref="valueElem"
        @blur="amountBlur"
        @focus="amountFocus"
        @keyenter="emit('submit')"
      />
    </div>

    <button
      class="btn-primary"
      @click="emit('submit')"
    >&check;</button>
    <button
      @click="emit('cancel')"
    >&#x2717;</button>
  </div>
</template>

<style>
.rma-adjustment-form {
  display: grid;
  grid-auto-flow: column;
  gap: 8px;
}

.rma-adjustment-form__amount {
  width: 100px;
}

.rma-adjustment-form__amount input {
  text-align: right;
}
</style>
